import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/creatorApi";
import { VERSIONS, sendToStudio } from "@/lib/enhanceApi";
import { safeApiError } from "@/lib/safeErrorText";

const NEW = "__new";
const sel = "w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white focus:border-amber-300/50 focus:outline-none";
const lbl = "mb-1 mt-4 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50";
const get = (p) => axios.get(`${API}/rec-studio${p}`, { withCredentials: true }).then((r) => r.data);

/** Put one version on a Recording Studio track as a new take (lossless, starts at 0:00). */
export default function SendToStudio({ open, onClose, item, version }) {
  const [v, setV] = useState(version);
  const [sessions, setSessions] = useState(null);
  const [sid, setSid] = useState(NEW);
  const [tracks, setTracks] = useState([]);
  const [tid, setTid] = useState(NEW);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (open) { setV(version); setDone(null); get("/sessions").then(setSessions).catch(() => setSessions([])); } }, [open, version]);
  useEffect(() => {
    setTid(NEW);
    setTracks([]);
    if (sid !== NEW) get(`/sessions/${sid}`).then((s) => setTracks(s.tracks || [])).catch(() => setTracks([]));
  }, [sid]);
  const send = async () => {
    setBusy(true);
    try {
      const r = await sendToStudio(item.id, { version: v, session_id: sid === NEW ? null : sid, track_id: tid === NEW ? null : tid });
      setDone(r);
      toast.success(`Sent to “${r.session_title}”`, { description: `New take on ${r.track_name}.` });
    } catch (e) { toast.error("Couldn't send it", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="send-studio-dialog" className="max-w-md border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Send className="h-4 w-4 text-amber-200" /> Send to Recording Studio</DialogTitle>
          <DialogDescription className="text-white/60">Lands as a new take starting at 0:00, so it lines up with your music.</DialogDescription>
        </DialogHeader>
        <label className={lbl} htmlFor="send-version">Version</label>
        <select id="send-version" data-testid="send-studio-version" className={sel} value={v} onChange={(e) => setV(e.target.value)}>
          {VERSIONS.filter((x) => item.versions?.[x.key]).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <label className={lbl} htmlFor="send-session">Recording session</label>
        {sessions === null ? <Loader2 className="h-4 w-4 animate-spin text-white/50" /> : (
          <select id="send-session" data-testid="send-studio-session" className={sel} value={sid} onChange={(e) => setSid(e.target.value)}>
            <option value={NEW}>New session · “{item.title}”</option>
            {sessions.map((s) => <option key={s.id} value={s.id}>{s.title}{s.instrumental ? ` · ${s.instrumental}` : ""}</option>)}
          </select>
        )}
        <label className={lbl} htmlFor="send-track">Track</label>
        <select id="send-track" data-testid="send-studio-track" className={sel} value={tid} onChange={(e) => setTid(e.target.value)}>
          <option value={NEW}>New track · “Enhanced vocal”</option>
          {tracks.map((t) => <option key={t.id} value={t.id}>{t.name} ({(t.takes || []).length} takes)</option>)}
        </select>
        {done ? (
          <div data-testid="send-studio-done" className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-3 text-[13px]">
            <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-300" /> Sent</span>
            <Link to={`/create/studio/${done.session_id}?setup=0`} data-testid="send-studio-open" className="rounded-full bg-emerald-300 px-3.5 py-1.5 font-semibold text-black">Open in studio</Link>
          </div>
        ) : (
          <button type="button" data-testid="send-studio-submit" onClick={send} disabled={busy || sessions === null}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-300 py-2.5 text-[13px] font-semibold text-black disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
